// @ts-nocheck
import React, { useState } from "react";
import { GlobalAdmin } from "coolremote-sdk";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CustomerReportsTab from "./components/CustomerReportsTab";
import UnitMappingSnapshotsTab from "./components/UnitMappingSnapshotsTab";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
const ExcelJS = require("exceljs");
import { saveAs } from "file-saver";
import useStyles from "./DaikinConsistency.style";
import ConsistencyView from "./components/ConsistencyView";
import VirtualSnapshotView from "./components/VirtualSnapshotView";
import CacheView from "./components/CacheView";

const SECTION_LABELS = {
  system: "System",
  control: "Indoor",
  service: "Service",
  outdoor: "Outdoor",
};

const SECTION_KEYS = ["system", "control", "service", "outdoor"];

const DaikinConsistency = (props) => {
  const classes = useStyles();
  const [serial, setSerial] = useState("");
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState(null);
  const [lineStatus, setLineStatus] = useState(null);

  const [consistencyResult, setConsistencyResult] = useState(null);
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [consistencyLine, setConsistencyLine] = useState(0);

  const [virtualResult, setVirtualResult] = useState(null);
  const [virtualLoading, setVirtualLoading] = useState(false);
  const [virtualLine, setVirtualLine] = useState(0);

  const [virtualConsistencyResult, setVirtualConsistencyResult] = useState(null);
  const [virtualConsistencyLoading, setVirtualConsistencyLoading] = useState(false);
  const [virtualConsistencyLine, setVirtualConsistencyLine] = useState(0);

  const [cacheResult, setCacheResult] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [cacheLine, setCacheLine] = useState(0);

  const handleSearch = async () => {
    if (!serial.trim()) { return; }
    setSearching(true);
    setError(null);
    setDevices([]);
    setSelectedDevice(null);
    setLineStatus(null);
    setConsistencyResult(null);
    setVirtualResult(null);
    setVirtualConsistencyResult(null);
    setCacheResult(null);
    setMode(null);
    try {
      const response = await GlobalAdmin.searchDaikinDevices(serial.trim());
      const results = response?.data || response || [];
      setDevices(results);
      if (results.length === 1) {
        setSelectedDevice(results[0].device);
        setLineStatus(results[0].lineStatus);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to search devices");
    } finally {
      setSearching(false);
    }
  };

  const runConsistency = async (device) => {
    setConsistencyLoading(true);
    setError(null);
    setConsistencyResult(null);
    setConsistencyLine(0);
    try {
      const response = await GlobalAdmin.getDaikinConsistency(device.serial);
      setConsistencyResult(response?.data || response);
    } catch (e) {
      setError(e?.message || "Failed to fetch consistency data");
    } finally {
      setConsistencyLoading(false);
    }
  };

  const runVirtualSnapshot = async (device) => {
    setVirtualLoading(true);
    setError(null);
    setVirtualResult(null);
    setVirtualLine(0);
    try {
      const response = await GlobalAdmin.getDaikinVirtualSnapshot(device.serial);
      setVirtualResult(response?.data || response);
    } catch (e) {
      setError(e?.message || "Failed to fetch virtual snapshot");
    } finally {
      setVirtualLoading(false);
    }
  };

  const runVirtualConsistency = async (device) => {
    setVirtualConsistencyLoading(true);
    setError(null);
    setVirtualConsistencyResult(null);
    setVirtualConsistencyLine(0);
    try {
      const response = await GlobalAdmin.getDaikinVirtualConsistency(device.serial);
      setVirtualConsistencyResult(response?.data || response);
    } catch (e) {
      setError(e?.message || "Failed to fetch virtual consistency");
    } finally {
      setVirtualConsistencyLoading(false);
    }
  };

  const runCache = async (device) => {
    setCacheLoading(true);
    setError(null);
    setCacheResult(null);
    setCacheLine(0);
    try {
      const response = await GlobalAdmin.getDeviceCache(device.id);
      setCacheResult(response?.data || response);
    } catch (e) {
      setError(e?.message || "Failed to fetch device cache");
    } finally {
      setCacheLoading(false);
    }
  };

  const handleDeviceSelect = (entry) => {
    setConsistencyResult(null);
    setVirtualResult(null);
    setVirtualConsistencyResult(null);
    setCacheResult(null);
    setMode(null);
    setSelectedDevice(entry.device);
    setLineStatus(entry.lineStatus);
  };

  const handleModeChange = async (newMode) => {
    setMode(newMode);
    if (!selectedDevice) { return; }
    if (newMode === "consistency" && !consistencyLoading) {
      await runConsistency(selectedDevice);
    } else if (newMode === "virtual" && !virtualLoading) {
      await runVirtualSnapshot(selectedDevice);
    } else if (newMode === "virtualConsistency" && !virtualConsistencyLoading) {
      await runVirtualConsistency(selectedDevice);
    } else if (newMode === "cache" && !cacheLoading) {
      await runCache(selectedDevice);
    }
  };

  const [topTab, setTopTab] = useState(0);

  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExport = async (issuesOnly = true) => {
    if (!consistencyResult) { return; }
    const workbook = new ExcelJS.Workbook();
    const columns = [
      { header: "Type", key: "entityType", width: 10 },
      { header: "Name", key: "name", width: 28 },
      { header: "Connected", key: "connected", width: 12 },
      { header: "Priority", key: "priority", width: 10 },
      { header: "Description", key: "description", width: 52 },
    ];
    for (const [lineId, line] of Object.entries(consistencyResult.lines)) {
      const sheet = workbook.addWorksheet(`Line ${lineId}`);
      sheet.columns = columns.map((c) => ({ header: c.header, key: c.key, width: c.width }));
      sheet.getRow(1).font = { bold: true };
      for (const section of SECTION_KEYS) {
        for (const [entityKey, entity] of Object.entries(line[section])) {
          const addr = entity.address ?? entityKey;
          const nameCol = entity.name ? `${entity.name} (${addr})` : `(${addr})`;
          if (issuesOnly && entity.issues.length === 0) { continue; }
          const base = {
            entityType: SECTION_LABELS[section],
            name: nameCol,
            connected: entity.isConnected ? "Yes" : "No",
          };
          if (entity.issues.length === 0) {
            sheet.addRow({ ...base, priority: "", description: "" });
          } else {
            for (const issue of entity.issues) {
              sheet.addRow({ ...base, priority: issue.priority, description: issue.description });
            }
          }
        }
      }
      sheet.getColumn("description").alignment = { wrapText: true };
    }
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: "application/xlsx" }), `dk-consistency-${consistencyResult.deviceSerial}.xlsx`);
  };

  const dkLineIds = lineStatus
    ? new Set([
        ...(lineStatus.currentDkLines || []).map((l) => String(l.lineId)),
        ...(lineStatus.pendingDkLines || []).map((l) => String(l.lineId)),
      ])
    : null;

  const isLoading =
    mode === "consistency"
      ? consistencyLoading
      : mode === "virtual"
        ? virtualLoading
        : mode === "virtualConsistency"
          ? virtualConsistencyLoading
          : mode === "cache"
            ? cacheLoading
            : false;

  const activeResult =
    mode === "consistency"
      ? consistencyResult
      : mode === "virtual"
        ? virtualResult
        : mode === "virtualConsistency"
          ? virtualConsistencyResult
          : mode === "cache"
            ? cacheResult
            : null;

  const deviceSerial =
    selectedDevice?.serial ||
    consistencyResult?.deviceSerial ||
    virtualResult?.deviceSerial ||
    virtualConsistencyResult?.deviceSerial ||
    "";

  const hasDevice = !!selectedDevice;
  const deviceNotRegistered = selectedDevice?.isRegistered === false;

  return (
    <ServiceNavigationBar title="DK Line Inspector" {...props}>
      <Box className={classes.pageContainer}>
        <Tabs value={topTab} onChange={(_, v) => setTopTab(v)} className={classes.tabsBar}>
          <Tab label="Device Inspector" />
          <Tab label="Customer Reports" />
          <Tab label="Unit Mapping Snapshots" />
        </Tabs>

        {topTab === 1 && <CustomerReportsTab />}
        {topTab === 2 && <UnitMappingSnapshotsTab />}
        {topTab === 0 && (
          <>
            <Box className={classes.searchRow}>
              <TextField
                size="small"
                label="Device serial"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className={classes.serialField}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searching || !serial.trim()}
                startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              >
                Search
              </Button>
              {deviceNotRegistered && (
                <Chip label="Not registered" color="warning" size="small" sx={{ ml: "auto", alignSelf: "center" }} />
              )}
            </Box>

            {devices.length > 1 && !selectedDevice && (
              <Paper className={classes.multiDevicePaper}>
                <Typography variant="subtitle2" mb={1}>
                  Multiple devices found — select one:
                </Typography>
                <Box className={classes.deviceButtonsBox}>
                  {devices.map((entry) => (
                    <Button
                      key={entry.device.serial}
                      variant="outlined"
                      size="small"
                      onClick={() => handleDeviceSelect(entry)}
                    >
                      {entry.device.serial}
                    </Button>
                  ))}
                </Box>
              </Paper>
            )}

            {error && (
              <Alert severity="error" className={classes.errorAlert}>
                {error}
              </Alert>
            )}

            {hasDevice && (
              <Box className={classes.deviceActionsBox}>
                {lineStatus && (
                  <Alert
                    severity={
                      lineStatus.hasDk ? (lineStatus.currentDkLines?.length === 0 ? "warning" : "success") : "info"
                    }
                    className={classes.lineStatusAlert}
                  >
                    {lineStatus.message}
                  </Alert>
                )}
                {lineStatus?.hasDk && (
                  <Box className={classes.modeBarRow}>
                    <Box className={classes.modeButtonsGroup}>
                      <Button
                        size="small"
                        variant={mode === "consistency" ? "contained" : "outlined"}
                        disabled={deviceNotRegistered}
                        onClick={() => handleModeChange("consistency")}
                      >
                        Consistency Check
                      </Button>
                      <Button
                        size="small"
                        variant={mode === "virtual" ? "contained" : "outlined"}
                        disabled={deviceNotRegistered}
                        onClick={() => handleModeChange("virtual")}
                      >
                        Virtual Snapshot
                      </Button>
                      <Button
                        size="small"
                        variant={mode === "virtualConsistency" ? "contained" : "outlined"}
                        disabled={deviceNotRegistered}
                        onClick={() => handleModeChange("virtualConsistency")}
                      >
                        Virtual Consistency
                      </Button>
                      <Button
                        size="small"
                        variant={mode === "cache" ? "contained" : "outlined"}
                        onClick={() => handleModeChange("cache")}
                      >
                        Cache
                      </Button>
                    </Box>
                    <Box className={classes.rightControlsGroup}>
                      {mode === "consistency" && consistencyResult && (
                        <>
                          <ButtonGroup size="small" variant="outlined">
                            <Button startIcon={<FileDownloadIcon />} onClick={() => handleExport(true)}>
                              Issues only
                            </Button>
                            <Button
                              size="small"
                              sx={{ px: 0.5, minWidth: "unset" }}
                              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                            >
                              <ArrowDropDownIcon fontSize="small" />
                            </Button>
                          </ButtonGroup>
                          <Menu
                            anchorEl={exportMenuAnchor}
                            open={Boolean(exportMenuAnchor)}
                            onClose={() => setExportMenuAnchor(null)}
                          >
                            <MenuItem
                              onClick={() => {
                                setExportMenuAnchor(null);
                                handleExport(false);
                              }}
                            >
                              Export all units
                            </MenuItem>
                          </Menu>
                        </>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {isLoading && (
              <Box className={classes.loadingBox}>
                <CircularProgress />
              </Box>
            )}

            {!isLoading && mode === "consistency" && consistencyResult && (
              <ConsistencyView
                result={consistencyResult}
                selectedLine={consistencyLine}
                onLineChange={setConsistencyLine}
              />
            )}

            {!isLoading && mode === "virtual" && virtualResult && (
              <VirtualSnapshotView result={virtualResult} selectedLine={virtualLine} onLineChange={setVirtualLine} />
            )}

            {!isLoading && mode === "virtualConsistency" && virtualConsistencyResult && (
              <ConsistencyView
                result={virtualConsistencyResult}
                selectedLine={virtualConsistencyLine}
                onLineChange={setVirtualConsistencyLine}
              />
            )}

            {!isLoading && mode === "cache" && cacheResult && (
              <CacheView
                result={cacheResult}
                selectedLine={cacheLine}
                onLineChange={setCacheLine}
                dkLineIds={dkLineIds}
              />
            )}
          </>
        )}
      </Box>
    </ServiceNavigationBar>
  );
};

export default DaikinConsistency;
